import React from "react";
import { usePermissions } from "../../../hooks";

/**
 * PermissionGate component - Conditionally renders children based on user permissions.
 * Used to show/hide UI elements based on RBAC permissions.
 *
 * @param {Object} props - Component props
 * @param {string} props.module - The module name (e.g., "users", "grade")
 * @param {string} props.action - The action name (e.g., "create", "read", "update", "delete")
 * @param {React.ReactNode} props.children - Content to render if permission is granted
 * @param {React.ReactNode} [props.fallback=null] - Content to render if permission is denied
 *
 * @example
 * <PermissionGate module="users" action="create">
 *   <Button>Create User</Button>
 * </PermissionGate>
 */
export function PermissionGate({ module, action, children, fallback = null }) {
  const { hasPermission, loading } = usePermissions();

  if (loading) return null;
  if (!hasPermission(module, action)) return fallback;

  return <>{children}</>;
}

/**
 * CanCreate - Convenience wrapper for create permission
 *
 * @example
 * <CanCreate module="users">
 *   <Button>New User</Button>
 * </CanCreate>
 */
export function CanCreate({ module, children, fallback = null }) {
  return (
    <PermissionGate module={module} action="create" fallback={fallback}>
      {children}
    </PermissionGate>
  );
}

/**
 * CanRead - Convenience wrapper for read permission
 *
 * @example
 * <CanRead module="users">
 *   <UserDetails />
 * </CanRead>
 */
export function CanRead({ module, children, fallback = null }) {
  return (
    <PermissionGate module={module} action="read" fallback={fallback}>
      {children}
    </PermissionGate>
  );
}

/**
 * CanUpdate - Convenience wrapper for update permission
 *
 * @example
 * <CanUpdate module="users">
 *   <Button icon="pencil">Edit</Button>
 * </CanUpdate>
 */
export function CanUpdate({ module, children, fallback = null }) {
  return (
    <PermissionGate module={module} action="update" fallback={fallback}>
      {children}
    </PermissionGate>
  );
}

/**
 * CanDelete - Convenience wrapper for delete permission
 *
 * @example
 * <CanDelete module="users">
 *   <Button icon="trash" color="red">Delete</Button>
 * </CanDelete>
 */
export function CanDelete({ module, children, fallback = null }) {
  return (
    <PermissionGate module={module} action="delete" fallback={fallback}>
      {children}
    </PermissionGate>
  );
}

/**
 * CanAccess - Wrapper for checking if user has any permission for a module
 * Useful for menu item visibility
 *
 * @example
 * <CanAccess module="users">
 *   <MenuItem>Users</MenuItem>
 * </CanAccess>
 */
export function CanAccess({ module, children, fallback = null }) {
  const { canAccess, loading } = usePermissions();

  if (loading) return null;
  if (!canAccess(module)) return fallback;

  return <>{children}</>;
}
